import React, { useState, useMemo } from 'react';
import { 
  Users, 
  Server, 
  Clock, 
  TrendingUp, 
  ArrowRight,
  Circle,
  Square,
  BarChart3,
  Activity
} from 'lucide-react';
import { QueueingSystemParams } from '../types';
import { QueueingCalculator } from '../utils/queueingCalculations';

interface DiagramTabProps {
  params: QueueingSystemParams;
}

export const DiagramTab: React.FC<DiagramTabProps> = ({ params }) => {
  const [activeView, setActiveView] = useState<'pooled' | 'dedicated'>('pooled');

  // Calculate metrics for both configurations
  const pooledMetrics = useMemo(() => {
    return QueueingCalculator.calculateMetrics(params);
  }, [params]);

  const dedicatedMetrics = useMemo(() => {
    if (params.servers <= 1) return null;
    const comparison = QueueingCalculator.calculatePoolingComparison(params);
    return comparison.dedicated;
  }, [params]);

  // Animation classes for flow
  const flowAnimation = "animate-pulse";
  const serverBusyColor = pooledMetrics.utilization > 0.8 ? "bg-red-400" : 
                         pooledMetrics.utilization > 0.6 ? "bg-yellow-400" : "bg-green-400";

  const renderPooledDiagram = () => (
    <div className="space-y-8">
      {/* Title */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-200 mb-2">Pooled Queue System (G/G/{params.servers})</h3>
        <p className="text-gray-400 text-sm">Single queue feeding multiple servers</p>
      </div>

      {/* Diagram */}
      <div className="bg-dark-900 rounded-xl p-8 border border-dark-600">
        <div className="flex items-center justify-between">
          
          {/* Arrival Process */}
          <div className="flex flex-col items-center space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-xs text-gray-300">Arrivals</div>
              <div className="text-xs text-blue-400 font-semibold">λ = {params.arrivalRate}/hr</div>
              <div className="text-xs text-gray-400">CV = {params.arrivalCV}</div>
            </div>
          </div>

          {/* Flow Arrow */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-1 bg-blue-400 ${flowAnimation}`}></div>
            <ArrowRight className="w-6 h-6 text-blue-400" />
            <div className={`w-4 h-1 bg-blue-400 ${flowAnimation}`}></div>
          </div>

          {/* Queue */}
          <div className="flex flex-col items-center space-y-2">
            <div className="text-center mb-2">
              <div className="text-xs text-gray-300">Queue</div>
            </div>
            <div className="flex space-x-1">
              {/* Queue visualization */}
              {Array.from({ length: Math.min(Math.ceil(pooledMetrics.wipQueue), 8) }).map((_, i) => (
                <div key={i} className="w-3 h-8 bg-orange-400 rounded-sm"></div>
              ))}
              {pooledMetrics.wipQueue > 8 && (
                <div className="text-orange-400 text-xs self-center ml-1">
                  +{Math.ceil(pooledMetrics.wipQueue - 8)}
                </div>
              )}
            </div>
            <div className="text-xs text-orange-400 font-semibold">
              WIP: {pooledMetrics.wipQueue.toFixed(1)}
            </div>
            <div className="text-xs text-gray-400">
              Wait: {pooledMetrics.queueTime.toFixed(1)} min
            </div>
          </div>

          {/* Flow to Servers */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-1 bg-green-400 ${flowAnimation}`}></div>
            <ArrowRight className="w-6 h-6 text-green-400" />
            <div className={`w-4 h-1 bg-green-400 ${flowAnimation}`}></div>
          </div>

          {/* Servers */}
          <div className="flex flex-col items-center space-y-4">
            <div className="text-center">
              <div className="text-xs text-gray-300 mb-2">Servers ({params.servers})</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: params.servers }).map((_, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className={`w-12 h-12 ${serverBusyColor} rounded-lg flex items-center justify-center`}>
                    <Server className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">S{i + 1}</div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <div className="text-xs text-green-400 font-semibold">μ = {params.serviceRate}/hr</div>
              <div className="text-xs text-gray-400">CV = {params.serviceCV}</div>
              <div className="text-xs text-purple-400 font-semibold">
                ρ = {(pooledMetrics.utilization * 100).toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Output Flow */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-1 bg-purple-400 ${flowAnimation}`}></div>
            <ArrowRight className="w-6 h-6 text-purple-400" />
            <div className={`w-4 h-1 bg-purple-400 ${flowAnimation}`}></div>
          </div>

          {/* Departures */}
          <div className="flex flex-col items-center space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mb-2">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div className="text-xs text-gray-300">Departures</div>
              <div className="text-xs text-purple-400 font-semibold">
                TH = {pooledMetrics.throughput.toFixed(1)}/hr
              </div>
              <div className="text-xs text-gray-400">
                CT = {pooledMetrics.cycleTime.toFixed(1)} min
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
          <div className="flex items-center justify-between">
            <Clock className="w-5 h-5 text-blue-400" />
            <span className="text-lg font-semibold text-blue-400">
              {pooledMetrics.queueTime.toFixed(1)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Queue Time (min)</div>
        </div>
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
          <div className="flex items-center justify-between">
            <Activity className="w-5 h-5 text-purple-400" />
            <span className="text-lg font-semibold text-purple-400">
              {(pooledMetrics.utilization * 100).toFixed(1)}%
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Utilization</div>
        </div>
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
          <div className="flex items-center justify-between">
            <BarChart3 className="w-5 h-5 text-orange-400" />
            <span className="text-lg font-semibold text-orange-400">
              {pooledMetrics.wipQueue.toFixed(1)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Queue WIP</div>
        </div>
        <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
          <div className="flex items-center justify-between">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <span className="text-lg font-semibold text-green-400">
              {pooledMetrics.throughput.toFixed(1)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Throughput (/hr)</div>
        </div>
      </div>
    </div>
  );

  const renderDedicatedDiagram = () => {
    if (!dedicatedMetrics) {
      return (
        <div className="text-center py-12">
          <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400">Dedicated queues comparison requires multiple servers (m &gt; 1)</p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Title */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-200 mb-2">Dedicated Queue System ({params.servers} × G/G/1)</h3>
          <p className="text-gray-400 text-sm">Separate queue for each server</p>
        </div>

        {/* Diagram */}
        <div className="bg-dark-900 rounded-xl p-8 border border-dark-600">
          <div className="space-y-6">
            
            {/* Arrival Splitting */}
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div className="text-xs text-gray-300">Arrivals</div>
                <div className="text-xs text-blue-400 font-semibold">λ = {params.arrivalRate}/hr</div>
                <div className="text-xs text-gray-400">Split to {params.servers} queues</div>
              </div>
            </div>

            {/* Split Arrows */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 gap-2">
                {Array.from({ length: Math.min(params.servers, 4) }).map((_, i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <div className={`w-8 h-1 bg-blue-400 ${flowAnimation}`}></div>
                    <ArrowRight className="w-4 h-4 text-blue-400" />
                  </div>
                ))}
                {params.servers > 4 && (
                  <div className="text-center text-gray-400 text-xs">
                    ... +{params.servers - 4} more
                  </div>
                )}
              </div>
            </div>

            {/* Individual Server Lines */}
            <div className="grid grid-cols-1 gap-6">
              {Array.from({ length: Math.min(params.servers, 4) }).map((_, serverIndex) => (
                <div key={serverIndex} className="flex items-center justify-between bg-dark-800 rounded-lg p-4">
                  
                  {/* Individual Queue */}
                  <div className="flex flex-col items-center">
                    <div className="text-xs text-gray-300 mb-2">Queue {serverIndex + 1}</div>
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(Math.ceil(dedicatedMetrics.queueTime / 10), 5) }).map((_, i) => (
                        <div key={i} className="w-2 h-6 bg-orange-400 rounded-sm"></div>
                      ))}
                    </div>
                    <div className="text-xs text-orange-400 font-semibold mt-1">
                      λ/m = {(params.arrivalRate / params.servers).toFixed(1)}/hr
                    </div>
                  </div>

                  {/* Flow */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-4 h-1 bg-green-400 ${flowAnimation}`}></div>
                    <ArrowRight className="w-4 h-4 text-green-400" />
                  </div>

                  {/* Individual Server */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 ${serverBusyColor} rounded-lg flex items-center justify-center mb-1`}>
                      <Server className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-xs text-gray-400">Server {serverIndex + 1}</div>
                    <div className="text-xs text-green-400 font-semibold">
                      μ = {params.serviceRate}/hr
                    </div>
                  </div>

                  {/* Output */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-4 h-1 bg-purple-400 ${flowAnimation}`}></div>
                    <ArrowRight className="w-4 h-4 text-purple-400" />
                  </div>

                  {/* Individual Output */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-xs text-purple-400 font-semibold mt-1">
                      TH/m = {(dedicatedMetrics.throughput / params.servers).toFixed(1)}/hr
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center justify-between">
              <Clock className="w-5 h-5 text-blue-400" />
              <span className="text-lg font-semibold text-blue-400">
                {dedicatedMetrics.queueTime.toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Queue Time (min)</div>
          </div>
          <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center justify-between">
              <Activity className="w-5 h-5 text-purple-400" />
              <span className="text-lg font-semibold text-purple-400">
                {(dedicatedMetrics.utilization * 100).toFixed(1)}%
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Utilization</div>
          </div>
          <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center justify-between">
              <BarChart3 className="w-5 h-5 text-orange-400" />
              <span className="text-lg font-semibold text-orange-400">
                {dedicatedMetrics.totalWIP.toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Total WIP</div>
          </div>
          <div className="bg-dark-800 rounded-lg p-4 border border-dark-600">
            <div className="flex items-center justify-between">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-lg font-semibold text-green-400">
                {dedicatedMetrics.throughput.toFixed(1)}
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Throughput (/hr)</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-dark-800 p-1 rounded-lg border border-dark-600">
        <button
          onClick={() => setActiveView('pooled')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeView === 'pooled'
              ? 'bg-primary-600 text-white'
              : 'text-gray-400 hover:text-gray-200 hover:bg-dark-700'
          }`}
        >
          Pooled Queue (G/G/{params.servers})
        </button>
        <button
          onClick={() => setActiveView('dedicated')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeView === 'dedicated'
              ? 'bg-primary-600 text-white'
              : 'text-gray-400 hover:text-gray-200 hover:bg-dark-700'
          }`}
        >
          Dedicated Queues ({params.servers} × G/G/1)
        </button>
      </div>

      {/* Diagram Content */}
      {activeView === 'pooled' ? renderPooledDiagram() : renderDedicatedDiagram()}

      {/* Comparison Insights */}
      {params.servers > 1 && dedicatedMetrics && (
        <div className="bg-dark-800 rounded-xl p-6 border border-dark-600">
          <h4 className="text-lg font-semibold text-gray-200 mb-4">Configuration Comparison</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
              <h5 className="font-medium text-primary-400 mb-2">Queue Time Improvement</h5>
              <div className="text-2xl font-bold text-white">
                {(((dedicatedMetrics.queueTime - pooledMetrics.queueTime) / dedicatedMetrics.queueTime) * 100).toFixed(1)}%
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Pooled queue is {pooledMetrics.queueTime < dedicatedMetrics.queueTime ? 'faster' : 'slower'}
              </p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
              <h5 className="font-medium text-accent-400 mb-2">WIP Reduction</h5>
              <div className="text-2xl font-bold text-white">
                {(dedicatedMetrics.totalWIP - pooledMetrics.wip).toFixed(1)}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Items saved with pooled queue
              </p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-700">
              <h5 className="font-medium text-green-400 mb-2">Efficiency Gain</h5>
              <div className="text-2xl font-bold text-white">
                {pooledMetrics.utilization > dedicatedMetrics.utilization ? '+' : ''}{((pooledMetrics.utilization - dedicatedMetrics.utilization) * 100).toFixed(1)}%
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Utilization difference
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};